// Turns a test run — streamed from the telemetry ingest service or read from
// an uploaded .mdb — into the one shape the lab dashboard draws from.
//
// Input ("run shape"), shared by both sources:
//   channels: [{ col_index, name, unit, group_name }]
//   rows:     [{ timestamp, values: { [col_index]: number } }]
//   metadata: { TestName, UnitType, TestStyle, ... }
//
// Channels are keyed by their column number, not their name: names repeat on
// rigs with several power meters, and a name collision would silently merge
// two different measurements into one line.

const PHASE_SUFFIXES = {
  current: ['R', 'S', 'T', 'Avg'],
  power: ['R', 'S', 'T', 'Total'],
  voltage: ['R', 'S', 'T', 'Line']
}

// A three-phase meter logs four channels per quantity: phases I, II and III,
// then the total. The approved dashboard names them by phase letter, as the
// Python reader does ("Power #1 R", "S", "T", "Total").
const phaseSuffixes = (name) => {
  const n = name.toLowerCase()
  if (n.includes('current') || n.includes('factor')) return PHASE_SUFFIXES.current
  if (n.includes('power')) return PHASE_SUFFIXES.power
  return PHASE_SUFFIXES.voltage
}

// The lab agent sends the phase marks the calorimeter writes ("Power Ⅰ #1" ...
// "Power Σ #1"): the name without its mark, and which phase it is.
const PHASE_MARKS = { Ⅰ: 0, Ⅱ: 1, Ⅲ: 2, Σ: 3 }
const PHASE_MARK = /\s*([ⅠⅡⅢΣ])(?=\s|$)/
const splitPhase = (name) => {
  const mark = name.match(PHASE_MARK)
  if (!mark) return null
  return { base: name.replace(PHASE_MARK, '').replace(/\s{2,}/g, ' ').trim(), phase: PHASE_MARKS[mark[1]] }
}

// Phase marks become the dashboard's phase names. A single-phase meter's one
// channel ("Power Ⅰ #2") keeps the plain name ("Power #2").
const phaseNames = (channels) => {
  const phases = channels.map(c => splitPhase(c.name))
  const perBase = {}
  phases.forEach(p => { if (p) perBase[p.base] = (perBase[p.base] || 0) + 1 })
  return channels.map((c, i) => {
    const p = phases[i]
    if (!p) return c
    if (perBase[p.base] < 2) return { ...c, name: p.base }
    return { ...c, name: `${p.base} ${phaseSuffixes(p.base)[p.phase]}` }
  })
}

// Channel text read from an uploaded .mdb comes from a legacy code page, so
// non-ASCII bytes survive as mojibake. The degree and cubed signs are the only
// ones that carry meaning.
export const cleanText = (s) => {
  if (!s) return ''
  return String(s)
    .trim()
    .replace(/℃/g, '°C')
    .replace(/[^\x20-\x7E°³]+/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export const cleanUnit = (s) => {
  if (!s) return ''
  const u = String(s)
    .trim()
    .replace(/℃/g, '°C')
    .replace(/[^\x20-\x7E°³/]+/g, '°')
    .replace(/°{2,}/g, '°')
    .replace(/\s{2,}/g, ' ')
    .trim()
  return u === '°' ? '°C' : u
}

export const dedupeNames = (channels) => {
  const counts = {}
  channels.forEach(c => { counts[c.name] = (counts[c.name] || 0) + 1 })
  const seen = {}
  return channels.map(c => {
    if (counts[c.name] < 2) return c
    const i = seen[c.name] || 0
    seen[c.name] = i + 1
    const suffixes = phaseSuffixes(c.name)
    return { ...c, name: `${c.name} ${suffixes[i] || i + 1}` }
  })
}

const pad = (n) => String(n).padStart(2, '0')

// Lab PCs record wall-clock time with no zone, and every reader in the
// pipeline carries it as UTC. It is formatted from the UTC fields and handed
// to Plotly without a zone, so the chart shows the time the lab saw rather
// than shifting it into the viewer's browser zone.
export const wallClock = (ts) => {
  const d = ts instanceof Date ? ts : new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
}

// A channel counts as recorded once any reading moves off zero; the rest are
// unwired inputs and are hidden, as the approved dashboard does.
const ACTIVE_THRESHOLD = 0.001

// A run's channels in column order, with the names the dashboard shows
export const namedChannels = (channels) => dedupeNames(phaseNames(
  [...(channels || [])]
    .sort((a, b) => a.col_index - b.col_index)
    .map(c => ({
      col_index: c.col_index,
      name: c.name || `Channel ${c.col_index}`,
      unit: c.unit || '',
      group: c.group_name || 'Other'
    }))
))

export const createDataset = ({ channels, metadata }) => {
  const named = namedChannels(channels)

  const dataset = {
    meta: { ...(metadata || {}) },
    channels: {},
    keys: [],
    activeKeys: [],
    activeSet: new Set(),
    timestamps: [],
    series: {},
    lastTimestamp: null,
    lastTime: null
  }
  named.forEach(c => {
    const key = String(c.col_index)
    dataset.channels[key] = { id: c.col_index, name: c.name, unit: c.unit, group: c.group }
    dataset.keys.push(key)
    dataset.series[key] = []
  })
  return dataset
}

// Appends rows in time order and reports how many were new. Rows at or before
// the last one held are dropped, so a poll that overlaps the previous one
// cannot draw a point twice.
export const appendRows = (dataset, rows) => {
  const ordered = [...(rows || [])].sort((a, b) => toTime(a.timestamp) - toTime(b.timestamp))
  let appended = 0
  let newlyActive = false

  for (const row of ordered) {
    const t = toTime(row.timestamp)
    const label = wallClock(row.timestamp)
    if (label === null || (dataset.lastTime !== null && t <= dataset.lastTime)) continue

    dataset.timestamps.push(label)
    const values = row.values || {}
    for (const key of dataset.keys) {
      const raw = values[key]
      // A missing reading stays missing. Plotting it as zero would draw a
      // sudden drop the unit never made.
      const v = typeof raw === 'number' && Number.isFinite(raw) ? raw : null
      dataset.series[key].push(v)
      if (v !== null && !dataset.activeSet.has(key) && Math.abs(v) > ACTIVE_THRESHOLD) {
        dataset.activeSet.add(key)
        newlyActive = true
      }
    }
    dataset.lastTime = t
    dataset.lastTimestamp = row.timestamp instanceof Date ? row.timestamp.toISOString() : row.timestamp
    appended++
  }

  if (newlyActive) {
    dataset.activeKeys = dataset.keys.filter(k => dataset.activeSet.has(k))
  }
  return appended
}

export const buildDataset = (run) => {
  const dataset = createDataset(run)
  appendRows(dataset, run.rows)
  return dataset
}

// The most recent non-empty reading of a channel.
export const latestValue = (dataset, key) => {
  const s = dataset.series[key] || []
  for (let i = s.length - 1; i >= 0; i--) {
    if (s[i] !== null) return s[i]
  }
  return null
}

// A reading as the Readings view and sheet show it. The lab software keeps a
// display format per channel; until that is stored with the channel, the
// decimals follow the size of the number.
export const readingDecimals = (v) => {
  const a = Math.abs(v)
  if (a >= 1000) return 1
  if (a >= 10) return 2
  return 3
}

export const formatReading = (v) =>
  v === null || v === undefined ? '--' : Number.isInteger(v) ? String(v) : v.toFixed(readingDecimals(v))

const toTime = (ts) => (ts instanceof Date ? ts.getTime() : new Date(ts).getTime())
